import { createContext, useContext, ReactNode } from 'react';
import { useDynamicType, ContentSizeCategory } from '@/hooks/useDynamicType';

interface DynamicTypeContextValue {
  category: ContentSizeCategory;
  scale: number;
  isAccessibilitySize: boolean;
}

const DynamicTypeContext = createContext<DynamicTypeContextValue | undefined>(undefined);

interface DynamicTypeProviderProps {
  children: ReactNode;
}

/**
 * Provider que disponibiliza informações de Dynamic Type para toda a aplicação
 * Detecta e responde automaticamente às mudanças de preferência do iOS
 */
export function DynamicTypeProvider({ children }: DynamicTypeProviderProps) {
  const dynamicType = useDynamicType();

  return (
    <DynamicTypeContext.Provider value={dynamicType}>
      {children}
    </DynamicTypeContext.Provider>
  );
}

/**
 * Hook para acessar as preferências de Dynamic Type
 * Deve ser usado dentro de um DynamicTypeProvider
 */
export function useDynamicTypeContext() {
  const context = useContext(DynamicTypeContext);
  
  if (context === undefined) {
    throw new Error('useDynamicTypeContext must be used within a DynamicTypeProvider');
  }
  
  return context;
}

/**
 * HOC para componentes que precisam reagir ao Dynamic Type
 */
export function withDynamicType<P extends object>(
  Component: React.ComponentType<P & DynamicTypeContextValue>
) {
  return function DynamicTypeComponent(props: P) {
    const dynamicType = useDynamicTypeContext();
    return <Component {...props} {...dynamicType} />;
  };
}
